import {type Brand, hasValue} from 'web-app/util/typescript';

type LoginId = Brand<string, 'loginId'>;
type Context = Brand<string, 'context'>;
type SiteId = Brand<string, 'siteId'>;

/**
 * Extracts loginId and context from a participant string of the format: Famly.Login:Login|<loginId>|<context>|<siteId>
 *
 * @param participants Array of participant strings of the format: Famly.Login:Login|<loginId>|<context>|<siteId>
 * @returns Array of [loginId, context, siteId]
 */
export const extractDataFromParticipantsStrings = (participants: string[]) => {
    return participants
        .map(participant => {
            // Extract the loginId and context from the participant string
            // Note that we're discarding the part before the first vertical bar
            const [, loginId, context, siteId] = participant.split('|');

            if (hasValue(loginId) && hasValue(context)) {
                return [loginId, context, siteId] as [LoginId, Context, SiteId];
            } else {
                return null;
            }
        })
        .filter(hasValue);
};

/**
 * @param participants Potential participants for a conversation
 * @param selectedParticipants Already selected participants for a conversation
 * @returns Whether any of the potential participants have the same loginId but a different context as any of the selected participants
 */
export const areSelectedInDifferentContext = (participants: string[], selectedParticipants: string[]): boolean => {
    // No reason to check if either the potential participants or the selected participants are empty
    if (participants.length === 0 || selectedParticipants.length === 0) {
        return false;
    }

    // Extract the loginId and context from the participants
    const participantsLoginIdContextPairs = extractDataFromParticipantsStrings(participants);

    // Extract the loginId and context from the selected participants
    const selectedParticipantsLoginIdContextPairs = extractDataFromParticipantsStrings(selectedParticipants);

    // Again, if either the potential participants or the selected participants are empty, we short circuit
    if (participantsLoginIdContextPairs.length === 0 || selectedParticipantsLoginIdContextPairs.length === 0) {
        return false;
    }

    // Finally, check any of the selected participants have the same loginId but a different context.
    const participantsContainsSameLoginIdButDifferentContext = participantsLoginIdContextPairs.some(
        ([participantLoginId, participantContext]) =>
            selectedParticipantsLoginIdContextPairs.some(
                ([selectedLoginId, selectedContext]) =>
                    participantLoginId === selectedLoginId && participantContext !== selectedContext,
            ),
    );

    // If that's the case, then we can't select the participant
    return participantsContainsSameLoginIdButDifferentContext;
};
